import { useState } from "react";
import { MoreHorizontal, Flag, Ban, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REASONS = [
  { id: "spam", label: "Spam ou arnaque" },
  { id: "harassment", label: "Harcèlement ou intimidation" },
  { id: "hate", label: "Discours haineux" },
  { id: "sexual", label: "Contenu sexuel" },
  { id: "violence", label: "Violence" },
  { id: "copyright", label: "Violation de droits d'auteur" },
  { id: "other", label: "Autre" },
];

/** "⋯" menu to report a piece of content and/or block its author — required
 *  for apps that show user-generated content. Renders nothing for your own
 *  content. targetType: post | comment | track | user | live. */
export default function ReportMenu({ targetType, targetId, authorId, authorName, onBlocked, className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("menu"); // menu | reason
  const [busy, setBusy] = useState(false);

  if (!authorId || (user && user.id === authorId)) return null;

  const close = () => { setOpen(false); setStep("menu"); };
  const needLogin = () => { toast.error("Connecte-toi pour signaler ou bloquer."); return !user; };

  const report = async (reason) => {
    if (needLogin()) return;
    setBusy(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason });
      toast.success("Merci, ton signalement a été envoyé. Nous allons l'examiner.");
      close();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible d'envoyer le signalement.");
    } finally { setBusy(false); }
  };

  const block = async () => {
    if (needLogin()) return;
    if (!window.confirm(`Bloquer ${authorName || "cet utilisateur"} ? Tu ne verras plus ses publications ni ses commentaires.`)) return;
    setBusy(true);
    try {
      await api.post(`/users/${authorId}/block`);
      toast.success(`${authorName || "Utilisateur"} est bloqué.`);
      close();
      onBlocked && onBlocked();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible de bloquer.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 ${className}`}
        aria-label="Signaler ou bloquer"
        data-testid={`report-menu-${targetType}-${targetId}`}
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-md grid place-items-center p-4" onClick={close} data-testid="report-modal">
          <div className="w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl p-5 relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={close} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="Fermer">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
            {step === "menu" ? (
              <div className="space-y-2 pt-6">
                <button type="button" onClick={() => setStep("reason")} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white text-sm font-semibold" data-testid="report-open-reasons">
                  <Flag className="w-4 h-4 text-amber-400" /> Signaler ce contenu
                </button>
                <button type="button" onClick={block} disabled={busy} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 text-white text-sm font-semibold disabled:opacity-50" data-testid="report-block">
                  <Ban className="w-4 h-4 text-red-400" /> Bloquer {authorName || "cet utilisateur"}
                </button>
              </div>
            ) : (
              <div className="pt-6">
                <h3 className="text-lg font-black text-white">Pourquoi signaler ?</h3>
                <div className="mt-3 space-y-1.5">
                  {REASONS.map((r) => (
                    <button key={r.id} type="button" disabled={busy} onClick={() => report(r.id)} className="w-full text-left px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm text-zinc-200 disabled:opacity-50" data-testid={`report-reason-${r.id}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
